import styled from 'styled-components';
import { EmailOutline16, User16 } from '@elfsight/icons';
import { Section, SpacedElements } from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import { Icon } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { CLASS_COMPONENT, TEXT_ACTION_ELEMENT_FONT_STYLE } from '../../common';
import { ActionTextButton } from './action-text-button';
import { Text } from './text';
import { Title } from './title';

export function CustomerDataBlock({
  name,
  email,
  onEdit,
  color,
  actionColor,
  isActionDisabled = false
}: {
  onEdit: () => void;
  email: string;
  color: string;
  actionColor: string;
  name?: string;
  isActionDisabled?: boolean;
}) {
  const { t } = useLingui();

  const ActionTextButtonElement = (
    <ActionTextButton
      $color={actionColor}
      onClick={onEdit}
      aria-label="Edit user data"
      disabled={isActionDisabled}
    >
      <Text textStyle={TEXT_ACTION_ELEMENT_FONT_STYLE}>{t`Edit`}</Text>
    </ActionTextButton>
  );

  return (
    <Section
      $color={color}
      $classModifier="user-data"
      $disabled={isActionDisabled}
      $withBottomDivider
      $withoutTopPadding
    >
      <SpacedElements $classModifier="section-title">
        <Title>{t`Your Details`}</Title>
      </SpacedElements>

      <Show when={!!name}>
        <SpacedElements $classModifier="name">
          <InlineElementsWrapper $classModifier="name">
            <Icon component={User16} size={16} fill={color} />
            <Text>{name}</Text>
          </InlineElementsWrapper>
          {ActionTextButtonElement}
        </SpacedElements>
      </Show>

      <SpacedElements $classModifier="email">
        <InlineElementsWrapper $classModifier="email">
          <Icon component={EmailOutline16} size={16} fill={color} />
          <Text>{email}</Text>
        </InlineElementsWrapper>

        <Show when={!name}>{ActionTextButtonElement}</Show>
      </SpacedElements>
    </Section>
  );
}

const InlineElementsWrapper = styled.div.attrs<{
  $classModifier?: string;
}>(({ $classModifier }) => ({
  className: createReadableClass({
    component: CLASS_COMPONENT,
    element: 'inline-elements-wrapper',
    modifier: $classModifier
  })
}))`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;
